import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {isAdmin$, isDirector$} from 'models/auth';
import {
    userIdForRemoveConfirmation$,
    onChangeUserIdForRemoveConfirmation,
    onSetEditUser,
    removeUserFx,
} from 'models/profile/staff/table';
import {StaffAccessors as Accessors, StaffListFormattedRow, StaffListRawRow} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {fetchGridConfig, saveGridConfig} from '../../../../api/gridsConfig';
import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {
    Action,
    ActionsContainer,
    CancelConfirmButton,
    ConfirmRemoveFooter,
    CreateUserButton,
    SubmitConfirmButton,
} from './styled';
import {StaffGridTable} from './Table';

export function convertData(
    data: StaffListRawRow[] | undefined,
    t: TFunction,
    updateUser: () => void,
): StaffListFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [Accessors.id]: handleNil(elem.id),
        [Accessors.name]: handleNil(`${elem.firstName} ${elem.middleName || ''} ${elem.lastName}`),
        [Accessors.email]: handleNil(elem.email),
        [Accessors.phone]: handleNil(elem.phone),
        [Accessors.actions]: handleNil(elem, (value: StaffListRawRow) => (
            <ActionsContainer>
                <Action
                    onClick={() => {
                        onSetEditUser(elem);
                        updateUser();
                    }}
                >
                    <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                </Action>
                <Action
                    disabled={!!value.clientId}
                    onClick={() => onChangeUserIdForRemoveConfirmation(value.id)}
                >
                    <Icon source={TrashIcon} fillColor="#F54848" width={18} height={18} />
                </Action>
            </ActionsContainer>
        )),
    }));
}

const getConfig = (t: TFunction, isDirectorOrAdmin?: boolean) => [
    {
        id: Accessors.name,
        header: t('pages.profile.staff.name'),
    },
    {
        id: Accessors.email,
        header: t('pages.profile.staff.email'),
    },
    {
        id: Accessors.phone,
        header: t('pages.profile.staff.phone'),
        width: 300,
    },
    ...(isDirectorOrAdmin
        ? [
              {
                  id: Accessors.actions,
                  header: t('pages.profile.staff.actions'),
                  width: 60,
              },
          ]
        : []),
];

export const Staff = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const isDirector = useStore(isDirector$);
    const isAdmin = useStore(isAdmin$);
    const userId = useStore(userIdForRemoveConfirmation$);
    const isDirectorOrAdmin = useMemo(() => isDirector || isAdmin, [isDirector, isAdmin]);

    const CreateBtn = () => {
        if (!isDirectorOrAdmin) return null;
        return (
            <CreateUserButton
                variant={ButtonVariant.Text}
                onClick={() => history.push(ROUTE.STAFF_CREATE)}
            >
                + {t('pages.profile.staff.create.createBtn')}
            </CreateUserButton>
        );
    };

    const ConfirmRemoveUser = () => {
        const onRemoveUser = async () => {
            if (!userId) return;
            await removeUserFx(userId);
            onChangeUserIdForRemoveConfirmation(null);
        };
        const translate = (key: string) => t('pages.profile.staff.confirm.' + key);
        return (
            <Modal modalWidth={0} show={!!userId} hideCloseButton onClose={() => {}}>
                <p style={{textAlign: 'center'}}>{translate('message')}</p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => onChangeUserIdForRemoveConfirmation(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={() => onRemoveUser()}>
                        {translate('ok')}
                    </SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs headingContent={<CreateBtn />} />
                <ContentContainer noPaddings>
                    <StaffGridTable configFunc={getConfig} dataMapper={convertData} />
                </ContentContainer>
            </Wrapper>
            {userId && <ConfirmRemoveUser />}
        </Page>
    );
};
